import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Activity,
  BarChart3,
  DollarSign,
  Download,
  Loader2,
  MousePointerClick,
  RefreshCw,
  Repeat,
  Share2,
  UserPlus,
  Users,
} from 'lucide-react';

import { getAnalyticsOverview, type AnalyticsRange } from '../lib/api';
import { getAccessToken, type AdminAccount } from '../lib/auth';
import { BarList, DailyBars, LengthBars, Metric, SourceNotice, SplitBar, SubHeading, type BarListItem } from './charts';
import { EXPLAIN } from './explanations';
import { InfoTip } from './info-tip';
import { PeriodPicker } from './period-picker';
import {
  amountIn,
  formatClockTime,
  formatCount,
  formatDecimal,
  formatDuration,
  formatMoney,
  formatMoneyList,
  formatDateRange,
  formatPercent,
  humanizeLabel,
} from './format';
import type { AccountsSummary, AnalyticsOverview, EngagementBreakdown, RetentionSection, SubscriptionSummary } from './types';

const COLORS = {
  fern: '#2f6f58', fernLight: '#8bb7a2', blue: '#315f85', amber: '#b66f16', rose: '#a43b43', stone: '#cfc6b6',
};
const ENTRY_POINTS: Record<string, string> = {
  top_performers: 'Top Performers', watchlist: 'Watchlist', portfolio: 'Portfolio', profile: 'Profile', direct: 'Direct',
  '(not set)': 'Not recorded (earlier app versions)',
};
const REMINDER_OUTCOMES: Record<string, string> = {
  added: 'Added to Calendar',
  cancelled: 'Closed without saving',
  opened: 'Opened Calendar (Android)',
  denied: 'Calendar access denied',
  unavailable: 'Calendar unavailable',
  failed: 'Could not open Calendar',
};

export function AnalyticsPanel(props: { account: AdminAccount }) {
  const accountId = props.account.homeAccountId;
  const [range, setRange] = useState<AnalyticsRange>({ days: 30 });
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setIsLoading(true);
    setError('');
    try {
      const token = await getAccessToken();
      setOverview(await getAnalyticsOverview(token, range, refresh));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Analytics could not be loaded.');
    } finally {
      setIsLoading(false);
    }
  }, [range, accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="panel analytics-panel" aria-busy={isLoading}>
      <div className="panel-heading analytics-heading">
        <BarChart3 size={20} aria-hidden="true" />
        <div>
          <h2>Analytics</h2>
          <p>
            {overview
              ? `${formatDateRange(overview.start_date, overview.end_date)} · updated ${formatClockTime(overview.generated_at)}`
              : 'Acquisition, activation, retention, referral and revenue in one view'}
          </p>
        </div>
        <div className="analytics-controls">
          <PeriodPicker
            start={overview?.start_date ?? null}
            end={overview?.end_date ?? null}
            range={range}
            disabled={isLoading && !overview}
            onChange={setRange}
          />
          <button type="button" disabled={isLoading} onClick={() => void load(true)}>
            {isLoading ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <RefreshCw size={17} aria-hidden="true" />}
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {error ? <SourceNotice status="error" message={error} /> : null}
      {!overview && isLoading ? <p className="empty-state">Loading analytics…</p> : null}
      {overview ? <AnalyticsContent overview={overview} /> : null}
    </section>
  );
}

function AnalyticsContent({ overview }: { overview: AnalyticsOverview }) {
  const { acquisition, activation, retention, engagement, referral, revenue } = overview;
  const { accounts, subscriptions } = activation;
  const currency = revenue.ledger.reporting_currency;
  const officialProceeds = amountIn(revenue.app_store.proceeds, currency);

  return (
    <div className="analytics-layout">
      <div className="kpi-grid">
        <Metric
          label="Downloads"
          info={EXPLAIN.downloads}
          value={acquisition.status === 'ok' || acquisition.status === 'empty' ? formatCount(acquisition.downloads) : '—'}
          hint={acquisition.status === 'ok' ? `App Store · first opens ${formatCount(acquisition.first_opens)}` : 'App Store Connect'}
        />
        <Metric
          label="Launches"
          info={EXPLAIN.launches}
          value={formatCount(retention.sessions)}
          hint={retention.status === 'ok' ? `${formatCount(retention.active_users)} people opened the app` : 'Firebase Analytics'}
        />
        <Metric label="New accounts"
          info={EXPLAIN.newAccounts} value={formatCount(accounts.new_in_range)} hint={`${formatCount(accounts.total)} total`} />
        <Metric
          label="New subscriptions"
          info={EXPLAIN.newSubscriptions}
          value={formatCount(subscriptions.new_in_range)}
          hint={`${formatPercent(activation.account_to_subscription_rate)} of new accounts`}
        />
        <Metric
          label="Cancellations"
          info={EXPLAIN.cancellations}
          value={formatCount(subscriptions.auto_renew_disabled_in_range)}
          hint={`${formatCount(subscriptions.expirations_in_range)} ended · ${formatCount(subscriptions.refunds_in_range)} refunded`}
        />
        <Metric label="Active subscribers"
          info={EXPLAIN.activeSubscribers} value={formatCount(subscriptions.active)} hint={subscriptions.environments.join(' + ')} />
        <Metric
          label="Daily active users"
          info={EXPLAIN.dailyActiveUsers}
          value={formatCount(retention.dau)}
          hint={retention.status === 'ok' ? `${formatCount(retention.mau)} monthly · ${formatPercent(retention.stickiness)} stickiness` : 'Firebase Analytics'}
        />
        <Metric
          label="Avg session"
          info={EXPLAIN.avgSession}
          value={formatDuration(retention.avg_session_seconds)}
          hint={retention.status === 'ok' ? `${formatDecimal(retention.sessions_per_user)} sessions / user` : 'Firebase Analytics'}
        />
        <Metric
          label="Monthly run-rate"
          info={EXPLAIN.runRate}
          value={formatMoneyList(revenue.ledger.mrr_run_rate, currency)}
          hint="Active subscriptions × last price"
        />
        <Metric
          label="Proceeds"
          info={EXPLAIN.proceeds}
          value={revenue.app_store.status === 'ok' ? formatMoney(officialProceeds, currency) : formatMoneyList(revenue.ledger.gross, currency)}
          hint={revenue.app_store.status === 'ok' ? 'Official, after Apple commission' : 'Estimated gross from transactions'}
        />
      </div>

      <AudienceCard retention={retention} subscriptions={subscriptions} accounts={accounts} />

      <div className="aarrr-grid">
        <article className="aarrr-card">
          <CardHeading icon={<Download size={18} aria-hidden="true" />} step="Acquisition" info={EXPLAIN.acquisitionCard} title="Downloads" />
          <SourceNotice status={acquisition.status} message={acquisition.message} />
          {acquisition.daily.length > 0 ? (
            <DailyBars
              ariaLabel="Daily downloads"
              data={acquisition.daily}
              series={[
                { key: 'downloads', label: 'First-time', color: COLORS.fern },
                { key: 'redownloads', label: 'Re-downloads', color: COLORS.fernLight },
              ]}
              formatValue={formatCount}
            />
          ) : null}
          <div className="metric-row">
            <Metric label="First-time" value={formatCount(acquisition.downloads)} />
            <Metric label="Re-downloads" value={formatCount(acquisition.redownloads)} />
            <Metric label="First opens" value={formatCount(acquisition.first_opens)} hint="Firebase" />
          </div>
        </article>

        <article className="aarrr-card">
          <CardHeading icon={<UserPlus size={18} aria-hidden="true" />} step="Activation" info={EXPLAIN.activationCard} title="Accounts & subscriptions" />
          <DailyBars
            ariaLabel="Daily new accounts"
            data={accounts.daily}
            series={[{ key: 'value', label: 'New accounts', color: COLORS.blue }]}
            formatValue={formatCount}
          />
          <SourceNotice status={subscriptions.status} message={subscriptions.message} />
          <DailyBars
            ariaLabel="Daily subscription starts"
            data={subscriptions.daily}
            series={[
              { key: 'new', label: 'New paid', color: COLORS.fern },
              { key: 'trials', label: 'Trials', color: COLORS.amber },
            ]}
            formatValue={formatCount}
          />
          <div className="metric-row">
            <Metric label="Trials" value={formatCount(subscriptions.trials_in_range)} />
            <Metric label="Billing retry" value={formatCount(subscriptions.billing_retry)} />
            <Metric label="Auto-renew off" value={formatCount(subscriptions.auto_renew_disabled_in_range)} />
          </div>
          {activation.paywall_funnel.length > 0 ? (
            <>
              <SubHeading title="Paywall funnel" info={EXPLAIN.paywallFunnel} />
              <BarList items={funnelItems(activation.paywall_funnel)} tone="blue" />
            </>
          ) : null}
          <Breakdown title="Subscriptions by entry point" info={EXPLAIN.subscriptionSources}
            breakdown={engagement.subscription_sources} labels={ENTRY_POINTS} emptyText="No completed purchases in this range." />
        </article>

        <article className="aarrr-card">
          <CardHeading icon={<Activity size={18} aria-hidden="true" />} step="Retention" info={EXPLAIN.retentionCard} title="Launches & session time" />
          <SourceNotice status={retention.status} message={retention.message} />
          {retention.status === 'ok' ? (
            <>
              <DailyBars
                ariaLabel="Daily sessions"
                data={retention.daily}
                series={[{ key: 'sessions', label: 'Sessions (app launches)', color: COLORS.fern }]}
                formatValue={formatCount}
              />
              <DailyBars
                ariaLabel="Daily active users"
                data={retention.daily}
                series={[{ key: 'active_users', label: 'Active users', color: COLORS.blue }]}
                formatValue={formatCount}
              />
            </>
          ) : null}
          <div className="metric-row">
            <Metric label="Sessions" value={formatCount(retention.sessions)} />
            <Metric label="Engaged time / user" value={formatDuration(retention.engagement_seconds_per_user)} />
            <Metric label="Returning users" value={formatCount(retention.returning_users)} />
          </div>
          <div className="metric-row">
            <Metric label="DAU" value={formatCount(retention.dau)} />
            <Metric label="WAU" value={formatCount(retention.wau)} />
            <Metric label="MAU" value={formatCount(retention.mau)} />
          </div>
        </article>

        <article className="aarrr-card">
          <CardHeading icon={<Share2 size={18} aria-hidden="true" />} step="Referral" info={EXPLAIN.referralCard} title="Referred users" />
          <SourceNotice status={referral.status} message={referral.message} />
        </article>

        <LifecycleCard subscriptions={subscriptions} />

        <article className="aarrr-card aarrr-card-wide">
          <CardHeading icon={<DollarSign size={18} aria-hidden="true" />} step="Revenue" info={EXPLAIN.revenueCard} title="Subscription revenue" />
          <div className="revenue-columns">
            <div>
              <SubHeading title="Verified transactions (real time)" info={EXPLAIN.verifiedTransactions} />
              <SourceNotice status={revenue.ledger.status} message={revenue.ledger.message} />
              <DailyBars
                ariaLabel={`Daily gross revenue in ${currency}`}
                data={revenue.ledger.daily}
                series={[{ key: 'gross', label: `Gross ${currency}`, color: COLORS.fern }]}
                formatValue={(value) => formatMoney(value, currency)}
              />
              <div className="metric-row">
                <Metric label="Gross" value={formatMoneyList(revenue.ledger.gross, currency)} />
                <Metric label="Refunds" value={formatMoneyList(revenue.ledger.refunds, currency)} />
                <Metric label="Renewals" value={formatCount(subscriptions.renewals_in_range)} />
              </div>
            </div>
            <div>
              <SubHeading title="App Store proceeds (official)" info={EXPLAIN.appStoreProceeds} />
              <SourceNotice status={revenue.app_store.status} message={revenue.app_store.message} />
              {revenue.app_store.daily.length > 0 ? (
                <DailyBars
                  ariaLabel={`Daily App Store proceeds in ${currency}`}
                  data={revenue.app_store.daily}
                  series={[{ key: 'proceeds', label: `Proceeds ${currency}`, color: COLORS.blue }]}
                  formatValue={(value) => formatMoney(value, currency)}
                />
              ) : null}
              <div className="metric-row">
                <Metric label="Proceeds" value={formatMoneyList(revenue.app_store.proceeds, currency)} />
                <Metric label="Customer sales" value={formatMoneyList(revenue.app_store.customer_sales, currency)} />
                <Metric label="Subscription units" value={formatCount(revenue.app_store.subscription_units)} />
              </div>
            </div>
          </div>
        </article>
      </div>

      <article className="aarrr-card engagement-card">
        <CardHeading icon={<MousePointerClick size={18} aria-hidden="true" />} step="Engagement" info={EXPLAIN.engagementCard} title="In-app behavior" />
        <SourceNotice status={engagement.status} message={engagement.message} />
        {engagement.status !== 'not_configured' ? (
          <div className="engagement-grid">
            <div className="engagement-wide">
              <Breakdown title="Time by app section" info={EXPLAIN.appSections} breakdown={engagement.app_sections} byTime />
            </div>
            <div className="engagement-wide">
              <SubHeading title="Time on screen" info={EXPLAIN.timeOnScreen} />
              <ScreenTable breakdown={engagement.screens} />
            </div>
            <Breakdown title="Time in ticker sections" info={EXPLAIN.tickerSections} breakdown={engagement.ticker_sections} byTime />
            <EarningsReminders engagement={engagement} />
            <Breakdown title="Return column sorted" info={EXPLAIN.sortColumns} breakdown={engagement.sort_columns} tone="blue" uppercase />
            <Breakdown title="Chart range selected" info={EXPLAIN.chartRanges} breakdown={engagement.chart_ranges} tone="blue" uppercase />
            <Breakdown title="Financials tab / view" info={EXPLAIN.financialsTabs} breakdown={engagement.financials_tabs} />
            <Breakdown title="Paywall trigger" info={EXPLAIN.paywallTrigger} breakdown={engagement.paywall_features} tone="amber" />
            <Breakdown title="Tracked events" info={EXPLAIN.trackedEvents} breakdown={engagement.events} withUsers />
          </div>
        ) : null}
      </article>
    </div>
  );
}

function CardHeading(props: { icon: ReactNode; step: string; title: string; info: string }) {
  return (
    <header className="card-heading">
      {props.icon}
      <div>
        <span>{props.step}</span>
        <h3>
          {props.title}
          <InfoTip label={props.title} text={props.info} />
        </h3>
      </div>
    </header>
  );
}

function AudienceCard(props: { retention: RetentionSection; subscriptions: SubscriptionSummary; accounts: AccountsSummary }) {
  const { retention, subscriptions, accounts } = props;
  const types = retention.account_types;
  const signedIn = types?.items.find((item) => item.label === 'registered');
  const guests = types?.items.find((item) => item.label === 'anonymous');
  const groups = [
    { label: 'Signed in', usage: signedIn, color: COLORS.fern },
    { label: 'Guests', usage: guests, color: COLORS.amber },
  ];
  const free = Math.max(0, accounts.total - subscriptions.active);
  const sandbox = subscriptions.environments.includes('Sandbox');

  return (
    <article className="aarrr-card audience-card">
      <CardHeading icon={<Users size={18} aria-hidden="true" />} step="Audience" info={EXPLAIN.audienceCard} title="Who uses Ascensa" />
      <div className="revenue-columns">
        <div>
          <SubHeading title="Signed in vs guests" info={EXPLAIN.signedInVsGuests} />
          {types && types.status !== 'ok' ? <SourceNotice status={types.status} message={types.message} /> : null}
          {types?.status === 'ok' ? (
            <>
              <SplitBar
                ariaLabel="People who opened the app"
                formatValue={(value) => `${formatCount(value)} people`}
                segments={groups.map((group) => ({ label: group.label, value: group.usage?.users ?? 0, color: group.color }))}
              />
              <div className="table-scroll">
                <table className="data-table compare-table">
                  <thead>
                    <tr>
                      <th scope="col"><span className="visually-hidden">Group</span></th>
                      <th scope="col">Launches</th>
                      <th scope="col">Launches / person</th>
                      <th scope="col">Avg visit</th>
                      <th scope="col">Time / person</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map(({ label, usage, color }) => (
                      <tr key={label}>
                        <th scope="row"><i className="legend-dot" style={{ background: color }} aria-hidden="true" />{label}</th>
                        <td>{formatCount(usage?.sessions ?? 0)}</td>
                        <td>{usage?.users ? formatDecimal(usage.sessions / usage.users, 1) : '—'}</td>
                        <td>{formatDuration(usage ? usage.avg_session_seconds : null)}</td>
                        <td>{formatDuration(usage?.users ? usage.engagement_seconds / usage.users : null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
        <div>
          <SubHeading title="Subscribers vs free accounts" info={EXPLAIN.subscribersVsFree} />
          <SplitBar
            ariaLabel="Accounts by plan"
            formatValue={formatCount}
            segments={[
              { label: 'Subscribers', value: subscriptions.active, color: COLORS.fern },
              { label: 'Free accounts', value: free, color: COLORS.stone },
            ]}
          />
          <SourceNotice
            status="ok"
            message={`${formatCount(accounts.total)} accounts in total, right now.${sandbox ? ' Subscribers include sandbox test purchases.' : ''}`}
          />
        </div>
      </div>
    </article>
  );
}

function LifecycleCard({ subscriptions }: { subscriptions: SubscriptionSummary }) {
  const length = subscriptions.length;
  return (
    <article className="aarrr-card aarrr-card-wide">
      <CardHeading icon={<Repeat size={18} aria-hidden="true" />} step="Subscriptions" info={EXPLAIN.lifecycleCard}
        title="Cancellations & subscription length" />
      <div className="revenue-columns">
        <div>
          <SubHeading title="Cancellations in this period" info={EXPLAIN.cancellationsSection} />
          <div className="metric-row metric-row-two">
            <Metric label="Turned off renewal" value={formatCount(subscriptions.auto_renew_disabled_in_range)} hint="Keep access until the period ends" />
            <Metric label="Ended" value={formatCount(subscriptions.expirations_in_range)} hint="Access finished" />
            <Metric label="Refunded" value={formatCount(subscriptions.refunds_in_range)} hint="Refunded by Apple" />
            <Metric label="Billing retry" value={formatCount(subscriptions.billing_retry)} hint="Payment failing right now" />
          </div>
        </div>
        <div>
          <SubHeading title="How long people stay subscribed" info={EXPLAIN.subscriptionLength} />
          {!length || length.status !== 'ok' ? (
            <SourceNotice status={length?.status ?? 'empty'} message={length?.message ?? 'No subscriptions yet.'} />
          ) : (
            <>
              <div className="length-summary">
                <strong>{length.average_months == null ? '—' : `${formatDecimal(length.average_months, 1)} months`}</strong>
                <span>average paid · {formatCount(length.active)} of {formatCount(length.subscriptions)} still subscribed · all time</span>
              </div>
              <LengthBars buckets={length.buckets} colors={{ active: COLORS.fern, ended: COLORS.stone }} />
              <SourceNotice status="ok" message={length.message} />
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function EarningsReminders({ engagement }: { engagement: AnalyticsOverview['engagement'] }) {
  const taps = engagement.events.items.find((item) => item.label === 'earnings_reminder_tap');
  const known = engagement.events.status === 'ok' || engagement.events.status === 'empty';
  const outcomes = engagement.earnings_reminders;
  const total = outcomes.items.reduce((sum, item) => sum + item.count, 0);
  return (
    <div className="reminder-block">
      <SubHeading title="Earnings reminder taps" info={EXPLAIN.earningsReminders} />
      <div className="metric-row metric-row-two">
        <Metric label="Taps" value={known ? formatCount(taps?.count ?? 0) : '—'} />
        <Metric label="People" value={known ? formatCount(taps?.users ?? 0) : '—'} />
      </div>
      {outcomes.status === 'ok' || outcomes.status === 'empty' ? (
        <BarList
          tone="amber"
          emptyText="No reminder taps in this range."
          items={outcomes.items.map((item) => ({
            label: REMINDER_OUTCOMES[item.label] ?? humanizeLabel(item.label),
            value: item.count,
            display: formatCount(item.count),
            detail: total > 0 ? `${formatPercent(item.count / total)} of taps` : undefined,
          }))}
        />
      ) : (
        <SourceNotice status={outcomes.status} message={outcomes.message} />
      )}
    </div>
  );
}

function funnelItems(steps: { step: string; count: number }[]): BarListItem[] {
  const first = steps[0]?.count ?? 0;
  return steps.map((step, index) => ({
    label: step.step,
    value: step.count,
    display: formatCount(step.count),
    detail: index > 0 && first > 0 ? `${formatPercent(step.count / first)} of paywall views` : undefined,
  }));
}

function Breakdown(props: {
  title: string;
  info: string;
  breakdown: EngagementBreakdown;
  withSeconds?: boolean;
  withUsers?: boolean;
  uppercase?: boolean;
  /** Rank and display by time in view instead of count (falls back to counts when time is unavailable). */
  byTime?: boolean;
  labels?: Record<string, string>;
  emptyText?: string;
  tone?: 'fern' | 'blue' | 'amber';
}) {
  const { title, info, breakdown, withSeconds, withUsers, uppercase, labels, emptyText, tone } = props;
  const byTime = !!props.byTime && breakdown.items.some((item) => item.seconds != null);
  const total = breakdown.items.reduce((sum, item) => sum + item.count, 0);
  const totalSeconds = breakdown.items.reduce((sum, item) => sum + (item.seconds ?? 0), 0);
  const label = (raw: string) => labels?.[raw] ?? (uppercase ? raw.toUpperCase() : humanizeLabel(raw));
  const items = breakdown.items.map((item) => byTime ? {
    label: label(item.label),
    value: item.seconds ?? 0,
    display: formatDuration(item.seconds),
    detail: [
      totalSeconds > 0 ? `${formatPercent((item.seconds ?? 0) / totalSeconds)} of time` : null,
      `${formatCount(item.count)} view${item.count === 1 ? '' : 's'}`,
      item.count ? `${formatDuration((item.seconds ?? 0) / item.count)} avg` : null,
    ].filter(Boolean).join(' · '),
  } : {
    label: label(item.label),
    value: item.count,
    display: formatCount(item.count),
    detail: [
      total > 0 ? `${formatPercent(item.count / total)} of total` : null,
      withSeconds && item.seconds != null ? `${formatDuration(item.seconds)} in view · ${formatDuration(item.count ? item.seconds / item.count : null)} avg` : null,
      withUsers && item.users != null ? `${formatCount(item.users)} users` : null,
    ].filter(Boolean).join(' · ') || undefined,
  });
  if (byTime) items.sort((a, b) => b.value - a.value);
  return (
    <div>
      <SubHeading title={title} info={info} />
      <SourceNotice status={breakdown.status === 'empty' ? 'ok' : breakdown.status} message={breakdown.message} />
      {breakdown.status === 'error' || breakdown.status === 'not_configured' ? null : <BarList items={items} tone={tone} emptyText={emptyText} />}
    </div>
  );
}

function ScreenTable({ breakdown }: { breakdown: EngagementBreakdown }) {
  if (breakdown.status !== 'ok' && breakdown.status !== 'empty') {
    return <SourceNotice status={breakdown.status} message={breakdown.message} />;
  }
  if (breakdown.items.length === 0) return <p className="empty-state">No screen views for this range.</p>;
  const rows = [...breakdown.items].sort((a, b) => (b.seconds ?? 0) - (a.seconds ?? 0));
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Screen</th>
            <th scope="col">Views</th>
            <th scope="col">Users</th>
            <th scope="col">Total time</th>
            <th scope="col">Avg / view</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{humanizeLabel(row.label)}</th>
              <td>{formatCount(row.count)}</td>
              <td>{formatCount(row.users)}</td>
              <td>{formatDuration(row.seconds)}</td>
              <td>{formatDuration(row.count && row.seconds != null ? row.seconds / row.count : null)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
